import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Bookmark, ChevronDown, Plus, Share2, Trash2, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  useSavedViews,
  useCreateSavedView,
  useDeleteSavedView,
} from '../hooks/useSavedViews';
import type { SavedView } from '../types';

interface SavedViewsDropdownProps {
  feature: string;
  currentFilters: Record<string, unknown>;
  onLoadView: (filters: Record<string, unknown>) => void;
  hasActiveFilters?: boolean;
}

export function SavedViewsDropdown({
  feature,
  currentFilters,
  onLoadView,
  hasActiveFilters = false,
}: SavedViewsDropdownProps) {
  const { t } = useTranslation('intelligence');
  const [open, setOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [viewToDelete, setViewToDelete] = useState<SavedView | null>(null);
  const [newViewName, setNewViewName] = useState('');
  const [newViewShared, setNewViewShared] = useState(false);

  const { data: views = [], isLoading } = useSavedViews(feature);
  const createMutation = useCreateSavedView();
  const deleteMutation = useDeleteSavedView(feature);

  // Separate views into personal and shared
  const myViews = views.filter((v) => v.isOwner && !v.isShared);
  const sharedViews = views.filter((v) => v.isShared);

  const handleSelectView = (view: SavedView) => {
    onLoadView(view.filters);
    setOpen(false);
  };

  const handleCreateView = async () => {
    if (!newViewName.trim()) return;

    try {
      await createMutation.mutateAsync({
        name: newViewName.trim(),
        feature,
        filters: currentFilters,
        isShared: newViewShared,
      });
      setNewViewName('');
      setNewViewShared(false);
      setCreateDialogOpen(false);
    } catch (error) {
      console.error('Failed to create view:', error);
    }
  };

  const handleDeleteView = async () => {
    if (!viewToDelete) return;

    try {
      await deleteMutation.mutateAsync(viewToDelete.id);
      setViewToDelete(null);
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete view:', error);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, view: SavedView) => {
    e.stopPropagation();
    setViewToDelete(view);
    setDeleteDialogOpen(true);
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 gap-2">
            <Bookmark className="h-4 w-4" />
            {t('savedViews.title', 'Saved Views')}
            <ChevronDown className="h-3 w-3 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <Command>
            <CommandInput placeholder={t('savedViews.search', 'Search views...')} />
            <CommandList>
              <CommandEmpty>
                {isLoading
                  ? t('savedViews.loading', 'Loading...')
                  : t('savedViews.noViews', 'No saved views yet.')}
              </CommandEmpty>

              {/* My Views */}
              {myViews.length > 0 && (
                <CommandGroup heading={t('savedViews.myViews', 'My Views')}>
                  {myViews.map((view) => (
                    <CommandItem
                      key={view.id}
                      value={view.name}
                      onSelect={() => handleSelectView(view)}
                      className="justify-between"
                    >
                      <div className="flex items-center gap-2">
                        <User className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{view.name}</span>
                      </div>
                      <button
                        onClick={(e) => handleDeleteClick(e, view)}
                        className="opacity-0 group-hover:opacity-100 hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {/* Shared Views */}
              {sharedViews.length > 0 && (
                <>
                  {myViews.length > 0 && <CommandSeparator />}
                  <CommandGroup heading={t('savedViews.sharedViews', 'Shared Views')}>
                    {sharedViews.map((view) => (
                      <CommandItem
                        key={view.id}
                        value={view.name}
                        onSelect={() => handleSelectView(view)}
                        className="justify-between"
                      >
                        <div className="flex items-center gap-2">
                          <Share2 className="h-3.5 w-3.5 text-muted-foreground" />
                          <span>{view.name}</span>
                        </div>
                        {view.isOwner && (
                          <button
                            onClick={(e) => handleDeleteClick(e, view)}
                            className="opacity-0 group-hover:opacity-100 hover:text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}

              <CommandSeparator />

              {/* Save Current Filters */}
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    setOpen(false);
                    setCreateDialogOpen(true);
                  }}
                  disabled={!hasActiveFilters}
                  className={cn(!hasActiveFilters && 'opacity-50')}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('savedViews.saveCurrentFilters', 'Save Current Filters')}
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Create View Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('savedViews.createTitle', 'Save View')}</DialogTitle>
            <DialogDescription>
              {t('savedViews.createDescription', 'Save your current filters as a view for quick access.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="view-name">{t('savedViews.viewName', 'View Name')}</Label>
              <Input
                id="view-name"
                value={newViewName}
                onChange={(e) => setNewViewName(e.target.value)}
                placeholder={t('savedViews.viewNamePlaceholder', 'e.g., Overdue - Montreal Site')}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="share-view">{t('savedViews.shareWithTeam', 'Share with team')}</Label>
                <p className="text-sm text-muted-foreground">
                  {t('savedViews.shareDescription', 'Allow others in your account to use this view')}
                </p>
              </div>
              <Switch
                id="share-view"
                checked={newViewShared}
                onCheckedChange={setNewViewShared}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              {t('common:cancel', 'Cancel')}
            </Button>
            <Button
              onClick={handleCreateView}
              disabled={!newViewName.trim() || createMutation.isPending}
            >
              {createMutation.isPending
                ? t('common:saving', 'Saving...')
                : t('common:save', 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('savedViews.deleteTitle', 'Delete View')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('savedViews.deleteDescription', 'Are you sure you want to delete "{name}"? This action cannot be undone.', {
                name: viewToDelete?.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteView}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteMutation.isPending
                ? t('common:deleting', 'Deleting...')
                : t('common:delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
